import { Dialog } from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useCreateEmailTemplateStore } from "../../../emailTemplates/stores/Hooks";
import { SaveTemplateForm } from "../forms/SaveTemplateForm";
import { CreateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/createEmailTemplateRPC";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";

interface CreateNewTemplateDialogProps {
  open: boolean;
  projectId: string;
  closeDialog: (snackbarConfig?: { message: string; error?: boolean }) => void;
}

export const CreateNewTemplateDialog: React.FC<CreateNewTemplateDialogProps> =
  observer(({ open, projectId, closeDialog }) => {
    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();

    const createEmailTemplateStore = useCreateEmailTemplateStore();

    const handleCreateEmailTemplate = async (): Promise<void> => {
      try {
        createEmailTemplateStore.setProjectId(projectId);
        await createEmailTemplateStore.createEmailTemplate();

        if (createEmailTemplateStore.emailTemplateName.validationError) return;
        if (createEmailTemplateStore.createEmailTemplateRPCError) {
          switch (createEmailTemplateStore.createEmailTemplateRPCError) {
            case CreateEmailTemplateRPC.RPCError.InvalidProjectId:
              navigateTo.pageNotFound();
              break;
            case CreateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
              closeDialog({
                message: t("common.projectAlreadyArchivedErrorText"),
                error: true,
              });
              break;
            default:
              createEmailTemplateStore.createEmailTemplateRPCError satisfies never;
          }
          return;
        }

        closeDialog({
          message: t("emailCycles.createNewTemplateSuccessMessage"),
        });
      } catch {
        closeDialog({
          message: t("emailCycles.createNewTemplateUnexpectedErrorMessage"),
          error: true,
        });
      }
    };

    return (
      <Dialog
        open={open}
        title={t("emailCycles.createNewTemplate")}
        primaryButtonText={t("emailCycles.createNewTemplate")}
        onPrimaryButtonClick={handleCreateEmailTemplate}
        isPrimaryButtonDisabled={createEmailTemplateStore.disableSubmit}
        secondaryButtonText={t("common.cancel")}
        onSecondaryButtonClick={closeDialog}
        isSecondaryButtonDisabled={
          createEmailTemplateStore.loadingCreateEmailTemplate
        }
        disableBackdropClick={true}
      >
        <SaveTemplateForm
          templateName={createEmailTemplateStore.emailTemplateName}
          disableForm={createEmailTemplateStore.loadingCreateEmailTemplate}
        />
      </Dialog>
    );
  });
